/**
 * dictation-queue.ts
 * "Dictados esperando señal" (operator-dictation-queue). A dictation recorded
 * with no signal is kept on the phone (dictation-store.ts) and sent on its
 * own when the connection returns; once the server has processed it, Hoy
 * offers it to confirm. Only dictations: every other form needs server data
 * to be filled in at all, and its create endpoint has no protection against
 * a resend yet.
 *
 * Safe to resend: the clientIntakeId generated at recording time is the
 * idempotency key the backend already honors (uq_voice_intake_client_intake_id
 * — a replay returns the existing intake), so a send whose answer was lost
 * to the signal never creates a second intake.
 *
 * Send outcomes, per dictation, in recording order:
 *   - sent → 'ready', audio dropped, voiceIntakeId kept to confirm.
 *   - no connection (status 0), lapsed session (401) or a passing server
 *     problem (408, 429, 5xx) → stays 'waiting'; the pass stops there and
 *     the next trigger tries again.
 *   - any other 4xx rejection → 'failed' with the server's message; the
 *     worker retries or discards it from Hoy.
 * A 'ready' dictation is forgotten once its intake was confirmed or
 * discarded (from any device).
 */
import { ref } from 'vue';
import { ApiError, isUnreachable } from './http.js';
import { currentClaims } from './session.js';
import { getVoiceIntake, submitDictation } from './voice-intake.js';
import { indexedDbDictationStore, type DictationStore, type QueuedDictation } from './dictation-store.js';

export type { QueuedDictation } from './dictation-store.js';

/** The current user's dictations on this phone, oldest first. */
export const queuedDictations = ref<QueuedDictation[]>([]);

let store: DictationStore = indexedDbDictationStore;
let sending: Promise<void> | null = null;

/** Swap the backing store (tests) and start from an empty view of it. */
export function useDictationStore(next: DictationStore): void {
  store = next;
  sending = null;
  queuedDictations.value = [];
}

function currentOwner(): string | null {
  const claims = currentClaims.value;
  return claims?.scope === 'tenant' ? `${claims.tenantId}:${claims.sub}` : null;
}

async function ownDictations(): Promise<QueuedDictation[]> {
  const owner = currentOwner();
  if (!owner) return [];
  const rows = await store.all();
  return rows.filter((r) => r.owner === owner).sort((a, b) => a.recordedAt.localeCompare(b.recordedAt));
}

export async function refreshDictations(): Promise<void> {
  try {
    queuedDictations.value = await ownDictations();
  } catch {
    // Storage unavailable: nothing can be queued on this phone either.
    queuedDictations.value = [];
  }
}

export interface NewDictation {
  readonly clientIntakeId: string;
  readonly audio: Blob;
  readonly durationMs: number;
  readonly recordedAt: string;
  readonly assetId?: string;
}

export async function queueDictation(dictation: NewDictation): Promise<void> {
  const owner = currentOwner();
  if (!owner) throw new Error('No session to queue the dictation under.');
  await store.put({ ...dictation, owner, state: 'waiting' });
  await refreshDictations();
}

/** The row without the given optional fields (the audio once sent, a stale error). */
function withoutFields(row: QueuedDictation, fields: ReadonlyArray<'audio' | 'error'>): QueuedDictation {
  const copy: { -readonly [K in keyof QueuedDictation]: QueuedDictation[K] } = { ...row };
  for (const field of fields) delete copy[field];
  return copy;
}

/**
 * Write a send result back only if the worker did not discard the dictation
 * while it was in flight.
 */
async function putIfStillQueued(row: QueuedDictation): Promise<void> {
  const rows = await store.all();
  if (rows.some((r) => r.clientIntakeId === row.clientIntakeId)) await store.put(row);
}

/**
 * Worth waiting out rather than giving up: no connection, a lapsed session,
 * or a passing server problem (timeout, rate limit, 5xx — a weak signal or a
 * deploy). Only other 4xx rejections mark the dictation failed.
 */
function isStillOffline(err: unknown): boolean {
  return isUnreachable(err) || (err instanceof ApiError && err.status === 401);
}

async function sendOne(row: QueuedDictation): Promise<'continue' | 'stop'> {
  if (!row.audio) return 'continue';
  try {
    const intake = await submitDictation({
      audio: row.audio,
      clientIntakeId: row.clientIntakeId,
      durationMs: row.durationMs,
      ...(row.assetId ? { assetId: row.assetId } : {}),
    });
    await putIfStillQueued(withoutFields({ ...row, state: 'ready', voiceIntakeId: intake.id }, ['audio', 'error']));
    return 'continue';
  } catch (err) {
    if (isStillOffline(err)) return 'stop';
    const message = err instanceof ApiError ? err.message : 'No se pudo enviar el dictado.';
    await putIfStillQueued({ ...row, state: 'failed', error: message });
    return 'continue';
  }
}

async function forgetIfSettled(row: QueuedDictation): Promise<'continue' | 'stop'> {
  if (!row.voiceIntakeId) return 'continue';
  try {
    const intake = await getVoiceIntake(row.voiceIntakeId);
    if (intake.status === 'dispatched' || intake.status === 'discarded') await store.remove(row.clientIntakeId);
    return 'continue';
  } catch (err) {
    if (isStillOffline(err)) return 'stop';
    // 404: the intake is gone server-side; nothing left to confirm.
    if (err instanceof ApiError && err.status === 404) await store.remove(row.clientIntakeId);
    return 'continue';
  }
}

async function runSendPass(): Promise<void> {
  const owner = currentOwner();
  for (const row of await ownDictations()) {
    // Someone else signed in mid-pass: never send A's audio with B's cookie.
    if (currentOwner() !== owner) break;
    const outcome = row.state === 'waiting' ? await sendOne(row) : row.state === 'ready' ? await forgetIfSettled(row) : 'continue';
    if (outcome === 'stop') break;
  }
}

/** Send every waiting dictation of the current user. One pass at a time. */
export async function sendQueuedDictations(): Promise<void> {
  sending ??= runSendPass()
    .catch(() => undefined)
    .finally(() => {
      sending = null;
    });
  await sending;
  await refreshDictations();
}

export async function retryDictation(clientIntakeId: string): Promise<void> {
  const row = (await ownDictations()).find((r) => r.clientIntakeId === clientIntakeId);
  if (!row) return;
  await store.put(withoutFields({ ...row, state: 'waiting' }, ['error']));
  await sendQueuedDictations();
}

export async function discardDictation(clientIntakeId: string): Promise<void> {
  await store.remove(clientIntakeId);
  await refreshDictations();
}

const RETRY_EVERY_MS = 60_000;

/**
 * Keep trying in the background: on start, when the browser reports the
 * connection is back, when the app returns to the foreground, and every
 * minute while something is still waiting (the 'online' event alone misses
 * a weak signal that comes and goes).
 */
export function startDictationSync(): void {
  const trigger = (): void => {
    void sendQueuedDictations();
  };
  trigger();
  window.addEventListener('online', trigger);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') trigger();
  });
  setInterval(() => {
    if (queuedDictations.value.some((d) => d.state === 'waiting')) trigger();
  }, RETRY_EVERY_MS);
}
